import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {
  FormControl,
  HelpBlock as Message,
  Button as SearchButton
} from 'react-bootstrap';

import styles from './SimpleSearchBar.css';
import window from '../window.js';
import { getPassthroughProps } from '../componentHelpers.js';
import {
  Search as SearchIcon,
  Times as TimesIcon,
} from '../icons/Icons.jsx';

class SimpleSearchBar extends React.Component {
  constructor(props) {
    super(props);

    this.input = null;
  }

  focusTextInput() {
    this.input.focus();
  }

  handleKeyPress = (evt) => {
    if (evt.key === 'Enter') {
      evt.preventDefault();
      this.search();
    }
  }

  handleSearch = () => {
    this.focusTextInput();
    this.search();
  };

  search = () => {
    if (this.timeout) {
      window.clearTimeout(this.timeout);
      delete this.timeout;
    }
    let {
      onSearch
    } = this.props;
    onSearch();
  }

  handleChange = (event) => {
    this.change(event.target.value);
  }

  change = (value) => {
    let {
      onChange,
      autosearch,
      autosearchDelayMs
    } = this.props;
    onChange(value);
    if (autosearch) {
      if (this.timeout) {
        window.clearTimeout(this.timeout);
      }
      this.timeout = window.setTimeout(this.search, autosearchDelayMs);
    }
  }

  handleClear = () => {
    this.change('');
    this.props.onClear();
    this.focusTextInput();
  }

  render() {
    let {
      autoFocus,
      value,
      placeholder,
      className,
      onClear,
      formControlProps,
      message
    } = this.props;
    return (
      <div className={classnames(styles.container, className)} {...getPassthroughProps(this)}>
        <FormControl
          autoFocus={autoFocus}
          className="hx-search-bar"
          inputRef={ref => { this.input = ref; }}
          placeholder={placeholder}
          type="text"
          value={value}
          onChange={this.handleChange}
          onKeyPress={this.handleKeyPress}
          {...formControlProps}
        />
        {
          value !== ''
            ? <TimesIcon className="hx-search-clear-icon" onClick={this.handleClear} />
            : null
        }
        <SearchButton
          className="hx-search-button"
          onClick={this.handleSearch}
        >
          <SearchIcon className="hx-search-icon" />
        </SearchButton>
        {
          message
            ? <Message className='hx-search-message'>{message}</Message>
            : null
        }
      </div>
    );
  }
}

SimpleSearchBar.propTypes = {
  /**
   * The value displayed in the search bar.
   */
  value: PropTypes.string,

  /**
   * Called when the value changes for any reason. Takes the new value as an argument.
   */
  onChange: PropTypes.func,

  /**
   * Called when a search should be performed. Takes no arguments.
   */
  onSearch: PropTypes.func,

  /**
   * Called when the clear button is clicked. Takes no arguments.
   * Should not update value: handled by onChange.
   */
  onClear: PropTypes.func,

  /**
   * Boolean indicating whether or not autosearch should be performed.
   * Default: false
   */
  autosearch: PropTypes.bool,

  /**
   * Delay in milliseconds before onSearch is called after typing when autosearch is enabled.
   * Default: 500
   */
  autosearchDelayMs: PropTypes.number,

  /**
   * Whether to auto-focus the search box.
   */
  autoFocus: PropTypes.bool,

  /**
   * Extra class names to be appended to the root component.
   */
  className: PropTypes.string,

  /**
   * Placeholder text for the input.
   */
  placeholder: PropTypes.string,

  /**
   * Extra props to be appended to the underlying FormControl.
   */
  formControlProps: PropTypes.object,

  /**
   * Help text to be displayed under the search bar.
   */
  message: PropTypes.node
};

SimpleSearchBar.defaultProps = {
  autosearchDelayMs: 500,
  formControlProps: {},
  onChange: () => {},
  onSearch: () => {},
  onClear: () => {}
};

export default SimpleSearchBar;
